import { useState, useEffect } from "react";

const quotes = [
  {
    text: "A reader lives a thousand lives before he dies. The man who never reads lives only one.",
    author: "George R.R. Martin",
  },
  {
    text: "Not all readers are leaders, but all leaders are readers.",
    author: "Harry S. Truman",
  },
  {
    text: "The more that you read, the more things you will know. The more that you learn, the more places you'll go.",
    author: "Dr. Seuss",
  },
  {
    text: "Reading is to the mind what exercise is to the body.",
    author: "Joseph Addison",
  },
  {
    text: "We are what we repeatedly do. Excellence, then, is not an act but a habit.",
    author: "Aristotle",
  },
  {
    text: "Books are a uniquely portable magic.",
    author: "Stephen King",
  },
  {
    text: "I find television very educating. Every time somebody turns on the set, I go into the other room and read a book.",
    author: "Groucho Marx",
  },
  {
    text: "That's the thing about books. They let you travel without moving your feet.",
    author: "Jhumpa Lahiri",
  },
  {
    text: "Sleep is good, he said, and books are better.",
    author: "George R.R. Martin",
  },
  {
    text: "The secret of getting ahead is getting started.",
    author: "Mark Twain",
  },
  {
    text: "Small daily improvements are the key to staggering long-term results.",
    author: "Unknown",
  },
  {
    text: "Motivation is what gets you started. Habit is what keeps you going.",
    author: "Jim Ryun",
  },
  {
    text: "Once you learn to read, you will be forever free.",
    author: "Frederick Douglass",
  },
  {
    text: "There is no friend as loyal as a book.",
    author: "Ernest Hemingway",
  },
  {
    text: "Today a reader, tomorrow a leader.",
    author: "Margaret Fuller",
  },
  {
    text: "You don't have to be great to start, but you have to start to be great.",
    author: "Zig Ziglar",
  },
  {
    text: "The discipline you learn and character you build from setting and achieving a goal can be more valuable than the achievement of the goal itself.",
    author: "Bo Bennett",
  },
  {
    text: "In the case of good books, the point is not to see how many of them you can get through, but how many can get through to you.",
    author: "Mortimer J. Adler",
  },
  {
    text: "Show up every day — not for the big dramatic moments — but for the work of ordinary time.",
    author: "Patti Smith",
  },
  {
    text: "A book is a dream that you hold in your hand.",
    author: "Neil Gaiman",
  },
  {
    text: "Consistency is the foundation of virtue.",
    author: "Francis Bacon",
  },
  {
    text: "The world belongs to those who read.",
    author: "Rick Holland",
  },
  {
    text: "It is what you read when you don't have to that determines what you will be when you can't help it.",
    author: "Oscar Wilde",
  },
  {
    text: "Reading gives us someplace to go when we have to stay where we are.",
    author: "Mason Cooley",
  },
  {
    text: "One must always be careful of books, and what is inside them, for words have the power to change us.",
    author: "Cassandra Clare",
  },
  {
    text: "What we plant in the soil of contemplation, we shall reap in the harvest of action.",
    author: "Meister Eckhart",
  },
  {
    text: "The man who does not read has no advantage over the man who cannot read.",
    author: "Mark Twain",
  },
  {
    text: "Success is the sum of small efforts, repeated day in and day out.",
    author: "Robert Collier",
  },
  {
    text: "Each book was a world unto itself, and in it I took refuge.",
    author: "Alberto Manguel",
  },
  {
    text: "Ten pages a day. Every day. That's the whole plan.",
    author: "10 Pages a Day",
  },
];

function getTodayIndex() {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const diff = now.getTime() - start.getTime();
  const dayOfYear = Math.floor(diff / (1000 * 60 * 60 * 24));
  return dayOfYear % quotes.length;
}

export default function DailyQuote() {
  const [index, setIndex] = useState(getTodayIndex());
  const [animating, setAnimating] = useState(false);

  const quote = quotes[index];

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  const navigate = (dir: 1 | -1) => {
    if (animating) return;
    setAnimating(true);
    setTimeout(() => {
      setIndex((prev) => (prev + dir + quotes.length) % quotes.length);
      setAnimating(false);
    }, 220);
  };

  return (
    <section className="py-28 px-8 lg:px-24 bg-[#F4F3F0]">
      <div className="max-w-6xl mx-auto">
        {/* Header row */}
        <div className="flex items-end justify-between mb-14">
          <div>
            <span className="text-[#3A5544] text-xs font-semibold uppercase tracking-[0.2em] mb-4 block">
              Daily Quote
            </span>
            <h2 className="font-serif text-4xl lg:text-5xl text-[#1A1A1A] leading-[1.2]">
              Words worth<br />sitting with.
            </h2>
          </div>
          <p className="hidden lg:block text-[#BBB] text-xs tracking-wide">{today}</p>
        </div>

        {/* Quote card */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Main quote */}
          <div
            className={`lg:col-span-8 bg-[#111111] rounded-lg p-10 lg:p-14 flex flex-col justify-between min-h-[320px] transition-opacity duration-200 ${
              animating ? "opacity-0" : "opacity-100"
            }`}
          >
            <div className="w-8 h-8 flex items-center justify-center mb-8">
              <i className="ri-double-quotes-l text-[#6B9E8A] text-3xl" />
            </div>
            <blockquote className="font-serif text-2xl lg:text-3xl text-white leading-[1.55] mb-10 flex-1">
              {quote.text}
            </blockquote>
            <div className="flex items-center justify-between">
              <p className="text-[#6B9E8A] text-sm font-medium tracking-wide">— {quote.author}</p>
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <span
                    key={i}
                    className={`block w-1 h-1 rounded-full transition-colors duration-300 ${
                      i === index % 5 ? "bg-[#6B9E8A]" : "bg-white/15"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Side panel */}
          <div className="lg:col-span-4 flex flex-col gap-6">
            {/* Today badge */}
            <div className="bg-[#3A5544] rounded-lg p-8 flex flex-col justify-between flex-1">
              <div className="w-8 h-8 flex items-center justify-center mb-4">
                <i className="ri-calendar-check-line text-white/60 text-xl" />
              </div>
              <div>
                <p className="text-white/50 text-xs uppercase tracking-widest mb-2">Today&apos;s quote</p>
                <p className="font-serif text-white text-xl leading-snug">
                  Let this one land.
                </p>
                <p className="text-white/45 text-xs mt-3 leading-relaxed">
                  One new quote every day. Come back tomorrow for the next one.
                </p>
              </div>
            </div>

            {/* Navigation */}
            <div className="bg-white rounded-lg p-6 flex items-center justify-between">
              <p className="text-[#888] text-xs">Browse quotes</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate(-1)}
                  className="w-9 h-9 flex items-center justify-center rounded border border-[#E0DED9] text-[#555] hover:border-[#3A5544] hover:text-[#3A5544] transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line text-sm" />
                </button>
                <button
                  onClick={() => navigate(1)}
                  className="w-9 h-9 flex items-center justify-center rounded border border-[#E0DED9] text-[#555] hover:border-[#3A5544] hover:text-[#3A5544] transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-line text-sm" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile date */}
        <p className="lg:hidden text-[#BBB] text-xs tracking-wide mt-6 text-center">{today}</p>
      </div>
    </section>
  );
}
